import React, { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  LogOut,
  Copy,
  Link2,
  GitBranch,
  Shield,
  Server,
  BarChart3,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import clsx from 'clsx';

const getNavItems = (isAdmin) => {
  const items = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/brokers', icon: Link2, label: 'Brokers' },
    { path: '/copy', icon: GitBranch, label: 'Trading' },
    { path: '/performance', icon: BarChart3, label: 'Performance' },
    { path: '/infrastructure', icon: Server, label: 'Infrastructure' },
  ];
  if (isAdmin) {
    items.push({ path: '/admin', icon: Shield, label: 'Admin' });
  }
  return items;
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navItems = getNavItems(user?.is_admin);
  const { isOpen, closeSidebar, isCollapsed, toggleCollapse } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [location.pathname, closeSidebar]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 bottom-0 bg-bg-secondary border-r border-border flex flex-col z-50',
          'transition-all duration-300 ease-in-out',
          // Width based on collapsed state
          isCollapsed ? 'md:w-20' : 'md:w-64',
          'w-64', // Always full width on mobile
          // Mobile: slide in/out
          'md:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <NavLink to="/dashboard" className={clsx(
            "flex items-center gap-3",
            isCollapsed && "md:justify-center"
          )}>
            <div className="w-10 h-10 rounded-xl bg-accent-primary flex items-center justify-center flex-shrink-0">
              <Copy className="w-5 h-5 text-white" />
            </div>
            <span className={clsx(
              "text-xl font-bold text-text-primary tracking-tight transition-opacity duration-200",
              isCollapsed ? "md:hidden" : "md:block"
            )}>
              MIMIQ
            </span>
          </NavLink>
          {/* Close button - mobile only */}
          <button
            onClick={closeSidebar}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded-lg md:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
                  isCollapsed && 'md:justify-center md:px-3',
                  isActive
                    ? 'bg-bg-tertiary text-text-primary'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/50'
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className={clsx(
                "font-medium transition-opacity duration-200",
                isCollapsed ? "md:hidden" : "md:block"
              )}>
                {item.label}
              </span>
            </NavLink>
          ))}
        </nav>

        {/* Collapse Toggle - Desktop only */}
        <div className="hidden md:block px-3 py-2 border-t border-border">
          <button
            onClick={toggleCollapse}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* User Section */}
        <div className="p-3 border-t border-border">
          <div className={clsx(
            "flex items-center gap-3 p-3 bg-bg-tertiary rounded-xl",
            isCollapsed && "md:justify-center md:p-2"
          )}>
            <div className="w-10 h-10 rounded-lg bg-accent-primary flex items-center justify-center text-white font-semibold flex-shrink-0">
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className={clsx(
              "flex-1 min-w-0 transition-opacity duration-200",
              isCollapsed ? "md:hidden" : "md:block"
            )}>
              <div className="text-sm font-medium text-text-primary truncate">
                {user?.username || 'User'}
              </div>
              <div className="text-xs text-text-muted truncate">
                {user?.email || 'Not logged in'}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className={clsx(
                "p-2 text-text-muted hover:text-text-secondary hover:bg-bg-secondary rounded-lg transition-colors",
                isCollapsed && "md:hidden"
              )}
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
